import { isAxiosError } from 'axios';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';
import { useGetDatasourceWithSuspense } from '@/hooks/core/useGetDataSource';

export type UsePresetUpgradeProps = {
  name: string;
  upgradePresetVersion: string | null;
};

export const usePresetUpgrade = ({
  name,
  upgradePresetVersion,
}: UsePresetUpgradeProps) => {
  const { data: datasource } = useGetDatasourceWithSuspense({ name });

  if (!datasource.spec.usePreset) {
    throw new Error(`Datasource "${name}" does not have a preset`);
  }

  if (!datasource.spec.usePresetVersion) {
    // TODO: Enable once versions are included in the datasource
    // throw new Error(`Datasource "${name}" does not have a preset version`);
  }

  if (!upgradePresetVersion) {
    throw new Error('Upgrade requires a target preset');
  }

  // Validate that upgradePresetVersion is a number
  const upgradePresetVersionNumber = Number(upgradePresetVersion);
  if (isNaN(upgradePresetVersionNumber)) {
    throw new Error(
      `"${upgradePresetVersion}" is not a valid preset version, must be a number`,
    );
  }

  const {
    data: preset,
    isPending: isPresetPending,
    error: presetError,
  } = useGetDatasourcePreset({
    name: datasource.spec.usePreset,
    version: datasource.spec.usePresetVersion,
  });

  const {
    data: upgradePreset,
    isPending: isUpgradePresetPending,
    error: upgradePresetError,
  } = useGetDatasourcePreset({
    name: datasource.spec.usePreset,
    version: upgradePresetVersionNumber,
  });

  if (upgradePresetError || presetError) {
    const error = upgradePresetError || presetError;
    if (isAxiosError(error) && error.response?.status === 404) {
      throw new Error(`Preset "${upgradePresetVersion}" not found`);
    }
    throw new Error(
      error!.message ||
        `Error loading upgrade preset "${upgradePresetVersion}"`,
    );
  }

  return {
    preset,
    upgradePreset,
    isPresetPending,
    isUpgradePresetPending,
    presetError,
    upgradePresetError,
    datasource,
  };
};
